import React from 'react';
import _ from 'underscore';

import {Table} from 'notebook/tables/TableConstants';
import {loadingIndicatorAttrs} from 'notebook/util/ExportInstrumentation';
import {getStore} from 'redux-stores/Store';
import {WorkflowSelector} from 'workflows/reduceWorkflows';
import inputTableToTable from 'workflows/stage-entries/inputTableToTable';
import {StageRun} from 'workflows/WorkflowConstants';

/**
 * This component takes a stageEntryId and emits a hydrated Table object. It retrieves all of
 * its data from the store; no fetching is actually performed here.
 *
 * Note that much of this work is duplicated in InputTable.tsx. That component manages the UI
 * for a workflow input table. The table we output here is typically used for internal or
 * intermediate functionality, such as extracting samples or text for processing.
 */

export interface BaseInputTableRetrieverProps {
  stageEntryId: string;
  children: (props: Table | null) => JSX.Element | null;
}

export default function InputTableRetriever(props: BaseInputTableRetrieverProps): JSX.Element | null {
  const workflows = getStore().getState().models.workflows;
  const workflowSelector = new WorkflowSelector(workflows);
  const stageEntry = workflowSelector.getStageEntry(props.stageEntryId);
  const orderedInputSamples = workflowSelector.getOrderedInputSampleKeys(props.stageEntryId);
  if (stageEntry == null || orderedInputSamples == null) {
    return <div {...loadingIndicatorAttrs()}>Loading input table data...</div>;
  }

  const {inputTableConfig} = stageEntry;
  const stageRuns = workflowSelector.getOrderedStageRunsForEntry(props.stageEntryId);

  // if stageRuns is undefined then the stage entry does not have any runs associated.
  if (stageRuns === undefined) {
    return props.children(null);
  }

  // If there are no stage runs, there's no table to build.
  if (stageRuns.length === 0) {
    return props.children(null);
  }

  const stageRunSampleById = workflowSelector.getStageRunSampleById();
  const parentIdsByStageRunId = workflowSelector.getParentIdsByStageRunId(
    stageRuns.map((stageRun: StageRun) => stageRun.id)
  );

  // Now, the loading indicator for parentIdsByStageRunId is separate
  if (parentIdsByStageRunId == null) {
    return <div {...loadingIndicatorAttrs()}>Loading related run data...</div>;
  }

  const stageRunIds = [
    ...Object.keys(parentIdsByStageRunId),
    // eslint-disable-next-line no-restricted-syntax
    ..._.flatten(Object.values(parentIdsByStageRunId)),
  ];
  if (stageRunIds.length === 0) {
    return props.children(null);
  }
  const stageRunsById = workflowSelector.getStageRunsById(stageRunIds);
  return props.children(
    inputTableToTable({
      parentIdsByStageRunId,
      stageRunsById,
      stageRunSampleById,
      stageRuns,
      inputTableConfig,
      orderedInputSamples,
    })
  );
}
